import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  PublicMetric,
  MetricFilters,
  Metric,
  MetricTrend,
} from '@lnr-v2/types';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Global cache for public metrics - shared across all component instances
// Keyed by language for proper localization support
const metricsCache: Record<
  string,
  {
    data: PublicMetric[] | null;
    timestamp: number | null;
    promise: Promise<PublicMetric[]> | null;
  }
> = {};

// Cache duration: 5 minutes (300000ms)
const CACHE_DURATION = 5 * 60 * 1000;

// Helper to get or create cache for a language
const getCache = (lang: string) => {
  if (!metricsCache[lang]) {
    metricsCache[lang] = { data: null, timestamp: null, promise: null };
  }
  return metricsCache[lang];
};

interface UseMetricsOptions {
  autoFetch?: boolean;
  forceRefresh?: boolean; // Force a refresh even if cache is valid
  onError?: (error: Error) => void;
  lang?: 'en' | 'fr'; // Language for localized labels
}

export function usePublicMetrics(options: UseMetricsOptions = {}) {
  const {
    autoFetch = true,
    forceRefresh = false,
    onError,
    lang = 'en',
  } = options;

  const [metrics, setMetrics] = useState<PublicMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);
  const onErrorRef = useRef(onError);

  // Update ref when onError changes
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const fetchMetrics = useCallback(
    async (forceFetch = false, _retryCount = 0) => {
      const cache = getCache(lang);

      // Check if we have valid cached data
      const now = Date.now();
      const cacheIsValid =
        cache.timestamp && cache.data && now - cache.timestamp < CACHE_DURATION;

      // Use cached data if available and valid (unless force refresh)
      if (cacheIsValid && !forceFetch && !forceRefresh) {
        console.log(`Using cached metrics data for lang: ${lang}`);
        setMetrics(cache.data!);
        setError(null);
        return cache.data!;
      }

      // If a fetch is already in progress, wait for it
      if (cache.promise && !forceFetch) {
        console.log('Metrics fetch already in progress, waiting...');
        setLoading(true);
        try {
          const data = await cache.promise;
          if (isMountedRef.current) {
            setMetrics(data);
            setError(null);
          }
          return data;
        } catch (err) {
          if (isMountedRef.current) {
            setError(err as Error);
          }
          throw err;
        } finally {
          if (isMountedRef.current) {
            setLoading(false);
          }
        }
      }

      // Start a new fetch
      setLoading(true);
      setError(null);

      // Create a single promise that all components can await
      cache.promise = (async () => {
        try {
          const url = `${API_BASE_URL}/metrics/public?lang=${lang}`;

          const response = await fetch(url, {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            mode: 'cors',
            credentials: 'omit',
          });

          if (!response.ok) {
            const errorText = await response
              .text()
              .catch(() => 'Unknown error');
            throw new Error(
              `Failed to fetch metrics: ${response.status} ${response.statusText}. Response: ${errorText}`
            );
          }

          const data = await response.json();

          // Validate response
          if (!data.metrics || !Array.isArray(data.metrics)) {
            console.error('Invalid metrics response:', data);
            throw new Error(`Invalid metrics response format`);
          }

          // Update cache
          cache.data = data.metrics;
          cache.timestamp = Date.now();
          cache.promise = null;

          return data.metrics;
        } catch (err) {
          // Clear promise on error
          cache.promise = null;
          throw err;
        }
      })();

      try {
        const data = await cache.promise;
        if (isMountedRef.current) {
          setMetrics(data);
          setError(null);
        }
        return data;
      } catch (err) {
        const error = err as Error;
        console.error('Failed to fetch metrics:', error.message);
        if (isMountedRef.current) {
          setError(error);
          onErrorRef.current?.(error);
        }
        throw error;
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [forceRefresh, lang]
  );

  useEffect(() => {
    isMountedRef.current = true;

    if (autoFetch) {
      fetchMetrics();
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [autoFetch, fetchMetrics, lang]); // Refetch when language changes

  // Manual refetch that forces cache refresh
  const refetch = useCallback(() => {
    return fetchMetrics(true);
  }, [fetchMetrics]);

  const cache = getCache(lang);
  return {
    metrics,
    loading,
    error,
    refetch,
    cacheTimestamp: cache.timestamp,
    cacheExpiresIn: cache.timestamp
      ? Math.max(0, CACHE_DURATION - (Date.now() - cache.timestamp))
      : 0,
  };
}

// Admin metrics hook with authentication
export function useMetrics(
  filters?: MetricFilters,
  options: UseMetricsOptions = {}
) {
  const { autoFetch = true } = options;
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/metrics?${params}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch metrics');
      }

      const data = await response.json();
      setMetrics(data.metrics);
      setTotal(data.total);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (autoFetch) {
      fetchMetrics();
    }
  }, [autoFetch, fetchMetrics]);

  return {
    metrics,
    total,
    loading,
    error,
    refetch: fetchMetrics,
  };
}

export function useMetric(id: string | undefined) {
  const [metric, setMetric] = useState<Metric | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchMetric = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/metrics/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch metric');
      }

      const data = await response.json();
      setMetric(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchMetric();
    }
  }, [id, fetchMetric]);

  const updateMetric = useCallback(
    async (updates: Partial<Metric>) => {
      if (!id) return;

      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/metrics/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify(updates),
        });

        if (!response.ok) {
          throw new Error('Failed to update metric');
        }

        const data = await response.json();
        setMetric(data);
        return data;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  const deleteMetric = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/metrics/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete metric');
      }

      setMetric(null);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  return {
    metric,
    loading,
    error,
    refetch: fetchMetric,
    updateMetric,
    deleteMetric,
  };
}

export function useMetricTrend(
  id: string | undefined,
  period: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month'
) {
  const [trend, setTrend] = useState<MetricTrend | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTrend = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/metrics/${id}/trend?period=${period}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch metric trend');
      }

      const data = await response.json();
      setTrend(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id, period]);

  useEffect(() => {
    if (id) {
      fetchTrend();
    }
  }, [id, fetchTrend]);

  return {
    trend,
    loading,
    error,
    refetch: fetchTrend,
  };
}

export function useCreateMetric() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createMetric = useCallback(async (data: Partial<Metric>) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/metrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to create metric');
      }

      const metric = await response.json();

      // Clear all language caches when a new metric is created
      Object.keys(metricsCache).forEach((key) => {
        metricsCache[key] = { data: null, timestamp: null, promise: null };
      });

      return metric;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createMetric,
    loading,
    error,
  };
}
